import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Package, X, Minus, Plus } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { useUpdateProductBundle, ProductBundle } from "@/hooks/useProductBundles";

interface BundleItem {
  product_id: string;
  quantity: number;
  name: string;
  price: number;
}

interface EditBundleDialogProps {
  bundle: ProductBundle;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditBundleDialog({ bundle, open, onOpenChange }: EditBundleDialogProps) {
  const { data: products } = useProducts();
  const updateBundle = useUpdateProductBundle();

  const [name, setName] = useState(bundle.name);
  const [description, setDescription] = useState(bundle.description || "");
  const [bundlePrice, setBundlePrice] = useState(bundle.bundle_price.toString());
  const [currency, setCurrency] = useState(bundle.currency);
  const [isActive, setIsActive] = useState(bundle.is_active);
  const [items, setItems] = useState<BundleItem[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>("");

  useEffect(() => {
    setName(bundle.name);
    setDescription(bundle.description || "");
    setBundlePrice(bundle.bundle_price.toString());
    setCurrency(bundle.currency);
    setIsActive(bundle.is_active);
    setItems(
      bundle.items?.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        name: item.product?.name || "Unknown",
        price: item.product?.base_price || 0,
      })) || []
    );
  }, [bundle]);

  const availableProducts = useMemo(() => {
    return products?.filter((p) => p.is_active && !items.find((i) => i.product_id === p.id)) || [];
  }, [products, items]);

  const originalPrice = useMemo(() => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [items]);

  const savings = useMemo(() => {
    const price = parseFloat(bundlePrice) || 0;
    return originalPrice - price;
  }, [originalPrice, bundlePrice]);

  const handleAddProduct = () => {
    if (!selectedProductId) return;
    const product = products?.find((p) => p.id === selectedProductId);
    if (!product) return;

    setItems([
      ...items,
      {
        product_id: product.id,
        quantity: 1,
        name: product.name,
        price: product.base_price,
      },
    ]);
    setSelectedProductId("");
  };

  const handleRemoveItem = (productId: string) => {
    setItems(items.filter((i) => i.product_id !== productId));
  };

  const handleQuantityChange = (productId: string, delta: number) => {
    setItems(
      items.map((item) =>
        item.product_id === productId
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await updateBundle.mutateAsync({
      id: bundle.id,
      name,
      description: description || null,
      bundle_price: parseFloat(bundlePrice) || 0,
      currency,
      discount_value: savings,
      is_active: isActive,
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Edit Bundle
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Bundle Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Product Selection */}
          <div className="space-y-2">
            <Label>Products in Bundle</Label>
            <div className="flex gap-2">
              <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} - ${product.base_price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                onClick={handleAddProduct}
                disabled={!selectedProductId}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {items.length > 0 && (
              <div className="space-y-2 mt-3 p-3 rounded-lg border bg-muted/50">
                {items.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex items-center justify-between py-2 px-3 bg-background rounded-md"
                  >
                    <div>
                      <span className="font-medium">{item.name}</span>
                      <span className="text-sm text-muted-foreground ml-2">
                        ${item.price.toFixed(2)} each
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleQuantityChange(item.product_id, -1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="w-8 text-center">{item.quantity}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleQuantityChange(item.product_id, 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleRemoveItem(item.product_id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-sm text-muted-foreground">Original Total</span>
                  <span className="font-medium">${originalPrice.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bundlePrice">Bundle Price *</Label>
              <Input
                id="bundlePrice"
                type="number"
                step="0.01"
                min="0"
                value={bundlePrice}
                onChange={(e) => setBundlePrice(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {items.length > 0 && bundlePrice && savings > 0 && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex justify-between items-center">
                <span className="text-green-600 dark:text-green-400 font-medium">
                  Customer Savings
                </span>
                <Badge className="bg-green-500/20 text-green-600 dark:text-green-400">
                  ${savings.toFixed(2)} ({((savings / originalPrice) * 100).toFixed(0)}% off)
                </Badge>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch id="isActive" checked={isActive} onCheckedChange={setIsActive} />
            <Label htmlFor="isActive">Active</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateBundle.isPending || items.length === 0}>
              {updateBundle.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
